import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSelfServe } from 'api/useSelfServe';
import { EChecklistTaskStatus, ETaskId } from 'common/contracts';
import { EEventsType } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import * as z from 'zod';

import Input from 'components/Input/Input';
import { Form, FormControl, FormField, FormItem } from 'components/ui';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';

import { VERIFY_BUSINESS_FORM_SCHEMA } from '../constants';

interface VerifyBusinessFormProps {
  onSuccess: () => void;
}

const AcceptTermsCheckbox: React.FC<{
  isAgreeToBeContacted: boolean;
  setIsAgreeToBeContacted: (isAgreeToBeContacted: boolean) => void;
}> = ({ isAgreeToBeContacted, setIsAgreeToBeContacted }) => (
  <div className="flex items-start space-x-2">
    <Checkbox
      id="contact-checkbox"
      checked={isAgreeToBeContacted}
      onCheckedChange={(checked) =>
        setIsAgreeToBeContacted(checked === 'indeterminate' ? false : checked)
      }
      className="data-[state=checked]:bg-black data-[state=checked]:text-white rounded-[4px]"
    />
    <label
      htmlFor="contact-checkbox"
      className="text-sm font-normal leading-none text-base-mutedForeground"
    >
      I agree to be contacted at this phone number and email for verification
      purposes.
    </label>
  </div>
);

export const VerifyBusinessForm: React.FC<VerifyBusinessFormProps> = ({
  onSuccess
}) => {
  const { publisherId } = useParams();
  const { verifyBusiness } = useSelfServe();
  const [isAgreeToBeContacted, setIsAgreeToBeContacted] = useState(false);
  const { trackBigQueryEvent } = useBigQueryEventsTracking();

  const initialValues = {
    fullName: '',
    phoneNumber: '',
    email: ''
  };

  const verifyBusinessFormFormConfig = useForm<
    z.infer<typeof VERIFY_BUSINESS_FORM_SCHEMA>
  >({
    resolver: zodResolver(VERIFY_BUSINESS_FORM_SCHEMA),
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = verifyBusinessFormFormConfig;

  const handleVerifyBusinessFormSubmit = (
    data: z.infer<typeof VERIFY_BUSINESS_FORM_SCHEMA>
  ) => {
    trackMixpanelEvent(EEventsType.DASHBOARD_BUSINESS_VERIFICATION_SUBMITTED, {
      phone: data.phoneNumber,
      publisher_id: publisherId,
      is_checkbox_approved: isAgreeToBeContacted
    });
    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_BUSINESS_VERIFICATION_SUBMITTED,
      publisherId,
      phone: data.phoneNumber,
      isCheckboxApproved: isAgreeToBeContacted,
      taskName: ETaskId.VERIFY_BUSINESS_CONTACT,
      taskStatus: EChecklistTaskStatus.OPEN
    });
    verifyBusiness.mutate(data, {
      onSuccess: () => {
        reset();
        setIsAgreeToBeContacted(false);
        onSuccess();
      },
      onError: () => {
        console.error('Error verifying business');
      }
    });
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <Form {...verifyBusinessFormFormConfig}>
          <form className="flex flex-col gap-6">
            <FormField
              name="fullName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      label="Full Name"
                      placeholder="Full Name"
                      isRequired={true}
                      state={errors.fullName?.message ? 'error' : 'default'}
                      description={errors.fullName?.message}
                      value={value}
                      onChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="phoneNumber"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      label="Phone Number"
                      placeholder="Enter your phone number"
                      isRequired={true}
                      state={errors.phoneNumber?.message ? 'error' : 'default'}
                      description={errors.phoneNumber?.message}
                      value={value}
                      onChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="email"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      label="Email"
                      placeholder="Enter your email"
                      isRequired={true}
                      state={errors.email?.message ? 'error' : 'default'}
                      description={errors.email?.message}
                      value={value}
                      onChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
        <AcceptTermsCheckbox
          isAgreeToBeContacted={isAgreeToBeContacted}
          setIsAgreeToBeContacted={setIsAgreeToBeContacted}
        />
      </div>
      <Button
        onClick={() => {
          handleSubmit(handleVerifyBusinessFormSubmit)();
        }}
        className="bg-base-primary hover:bg-button-hover-default text-base-primaryForeground font-normal"
        type="submit"
        disabled={!isValid || !isAgreeToBeContacted}
      >
        Submit
      </Button>
    </>
  );
};
